import type {
  ApolloQueryResult,
  Exact,
  InboxInsuredDetailFragment,
  InboxInsuredFragment,
  OpportunitiesInput,
  OpportunitiesQuery,
  PoliciesListQuery,
  QuotesQuery,
} from "@qw/graphql";
import { createContext, useContext } from "react";

type Opportunities = OpportunitiesQuery["opportunities"];
type Policies = PoliciesListQuery["policies"];
type Quotes = QuotesQuery["quotes"];

/**
 * The detail fields are `Partial` because the provider has two sources: the
 * `InboxInsured` query, which spreads `InboxInsuredDetail`, and the insured
 * hanging off the selected conversation, which does not — those fields are
 * deliberately kept out of `InboxConversation`'s payload. Partial rather than a
 * union so consumers that only want `id`/`name` are unchanged, and the ones that
 * want the detail have to say what they do without it.
 */
export type InboxInsured = InboxInsuredFragment & Partial<InboxInsuredDetailFragment>;

export interface InboxInsuredContextValue {
  activeOpportunities: Opportunities;
  activePolicies: Policies;
  draftQuotes: Quotes;
  insured: InboxInsured | null | undefined;
  insuredId: string | null | undefined;
  loading: boolean;
  opportunities: Opportunities;
  quotes: Quotes;
  refetchOpportunities: (
    variables?:
      | Partial<
          Exact<{
            input: OpportunitiesInput;
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<OpportunitiesQuery>>;
}

export const InboxInsuredContext = createContext<InboxInsuredContextValue | null>(null);

export const useInboxInsured = () => {
  const context = useContext(InboxInsuredContext);

  if (!context) {
    throw new Error("useInboxInsured must be used within an InsuredProvider");
  }

  return context;
};
